/**
 * Regras puras da árvore do explorer da Base de Conhecimento (spec 2026-09-26).
 *
 * O backend devolve as pastas numa lista plana (`GET /folders`); aqui elas viram
 * raízes por organização (`raizes`), e essas se agrupam nas empresas do cadastro
 * (`empresas`, spec 2026-10-21-kb-raizes). As pastas com `organization_id` nulo são
 * da raiz marcada `plataforma` ("Amazonas Inovare", D16 e R4).
 */
import type { KbDocument, KbFolderNode, KbLugar, KbOrgRotulo, KbRaiz, KbRaizes, KbSecao } from '@/api/kb'

export const ROTULO_DA_PLATAFORMA = 'Amazonas Inovare'

export interface Raiz {
  /** `null` = plataforma */
  organizationId: number | null
  rotulo: string
  pastas: KbFolderNode[]
}

/**
 * Nome de cada organização na KB: o rótulo, quando quem gere definiu um, senão o
 * nome da organização. O rótulo só existe aqui — o resto da plataforma usa o nome.
 */
export function nomesNaKb(
  orgs: { id: number; name: string }[],
  rotulos: KbOrgRotulo[],
): Map<number, string> {
  const nomes = new Map(orgs.map((o) => [o.id, o.name]))
  for (const r of rotulos) nomes.set(r.organization_id, r.rotulo)
  return nomes
}

function porNome(a: KbFolderNode, b: KbFolderNode): number {
  return a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' })
}

/** Filhas diretas de `parentId` entre os nós recebidos, em ordem de nome. */
export function filhas(nos: KbFolderNode[], parentId: number): KbFolderNode[] {
  return nos.filter((n) => n.parent_id === parentId).sort(porNome)
}

/**
 * Uma raiz por organização que tenha pasta, mais as de `orgsExtras` (quem gere
 * precisa da raiz vazia para criar a primeira pasta). A plataforma vem primeiro.
 * Pasta cujo pai não veio na lista sobe para o topo da raiz dela, para não sumir.
 */
export function raizes(
  nos: KbFolderNode[],
  nomesDeOrg: Map<number, string>,
  orgsExtras: number[] = [],
): Raiz[] {
  const ids = new Set(nos.map((n) => n.id))
  const topo = nos.filter((n) => n.parent_id === null || !ids.has(n.parent_id))
  const chaves = new Set<number | null>(topo.map((n) => n.organization_id))
  orgsExtras.forEach((o) => chaves.add(o))
  const lista: Raiz[] = [...chaves].map((org) => ({
    organizationId: org,
    rotulo: org === null ? ROTULO_DA_PLATAFORMA : (nomesDeOrg.get(org) ?? `Organização ${org}`),
    pastas: topo.filter((n) => n.organization_id === org).sort(porNome),
  }))
  return lista.sort((a, b) => {
    if (a.organizationId === null) return -1
    if (b.organizationId === null) return 1
    return a.rotulo.localeCompare(b.rotulo, 'pt-BR', { sensitivity: 'base' })
  })
}

/** Ainda vai mudar de status sozinho: a tela recarrega enquanto houver um assim. */
export function algumEmIndexacao(docs: KbDocument[]): boolean {
  return docs.some((d) => d.status === 'pending' || d.status === 'indexing')
}

/** Nome do nó que agrupa as organizações da seção `projetos` (spec das raízes, R3). */
export const NOME_DO_NO_PROJETOS = 'Projetos'

/** Raiz sem cadastro (a rota de raízes falhou): tudo cai na Amazonas Inovare › Projetos. */
export const RAIZ_SEM_CADASTRO = -1

/**
 * Empresa acima das organizações (spec 2026-10-21-kb-raizes, §3 e §8): as organizações
 * da seção `topo`, o nó "Projetos" e, na raiz da plataforma, as demais pastas sem
 * organização. A raiz não guarda documento (R2): é só agrupamento.
 */
export interface Empresa {
  raizId: number
  nome: string
  plataforma: boolean
  topo: Raiz[]
  /**
   * `pasta` é a pasta da plataforma "Projetos", fundida no nó (§8: não aparece duas
   * vezes); as subpastas dela (o ConsoleAI) vêm antes das organizações. `null` quando
   * a raiz não tem nada na seção.
   */
  projetos: { pasta: KbFolderNode | null; organizacoes: Raiz[] } | null
  /** Pastas da plataforma além da "Projetos" (Padrões de Desenvolvimento), por nome */
  pastasDaPlataforma: KbFolderNode[]
}

function porRotulo(a: Raiz, b: Raiz): number {
  return a.rotulo.localeCompare(b.rotulo, 'pt-BR', { sensitivity: 'base' })
}

function ehProjetos(p: KbFolderNode): boolean {
  return p.nome.trim().toLocaleLowerCase('pt-BR') === NOME_DO_NO_PROJETOS.toLocaleLowerCase('pt-BR')
}

/**
 * O cadastro como a tela o usa: todas as raízes (com a da plataforma garantida, mesmo
 * sem cadastro) e o lugar de cada organização. Organização sem lugar gravado, ou num
 * lugar de raiz que não veio, cai em `padrao` (R7).
 */
export function cadastroResolvido(cadastro: KbRaizes | undefined): {
  todas: KbRaiz[]
  plataforma: KbRaiz
  lugarDe: (organizationId: number) => KbLugar
} {
  const lista: KbRaiz[] = cadastro?.items ?? []
  const plataforma = lista.find((r) => r.plataforma)
    ?? { id: RAIZ_SEM_CADASTRO, nome: ROTULO_DA_PLATAFORMA, ordem: 0, plataforma: true }
  const todas = lista.some((r) => r.plataforma) ? lista : [plataforma, ...lista]
  const ids = new Set(todas.map((r) => r.id))
  const padrao: KbLugar = cadastro?.padrao && ids.has(cadastro.padrao.raiz_id)
    ? cadastro.padrao
    : { raiz_id: plataforma.id, secao: 'projetos' }
  const lugares = new Map((cadastro?.organizacoes ?? []).map((o) => [o.organization_id, o]))
  return {
    todas,
    plataforma,
    lugarDe: (org) => {
      const l = lugares.get(org)
      return l && ids.has(l.raiz_id) ? { raiz_id: l.raiz_id, secao: l.secao } : padrao
    },
  }
}

/**
 * Agrupa as raízes por organização (de `raizes`) nas empresas do cadastro. Empresa sem
 * nada só aparece para quem administra raízes: é onde ele põe a primeira organização.
 */
export function empresas(
  porOrg: Raiz[],
  cadastro: KbRaizes | undefined,
  { mostrarVazias = false }: { mostrarVazias?: boolean } = {},
): Empresa[] {
  const { todas, lugarDe } = cadastroResolvido(cadastro)

  const daPlataforma = porOrg.find((r) => r.organizationId === null)?.pastas ?? []
  const pastaProjetos = daPlataforma.find(ehProjetos) ?? null
  const orgs = porOrg.filter((r) => r.organizationId !== null)

  return todas
    .map((raiz): Empresa => {
      const daqui = orgs.filter((r) => lugarDe(r.organizationId!).raiz_id === raiz.id)
      const naSecao = (s: KbSecao) => daqui.filter((r) => lugarDe(r.organizationId!).secao === s).sort(porRotulo)
      const pasta = raiz.plataforma ? pastaProjetos : null
      const organizacoes = naSecao('projetos')
      return {
        raizId: raiz.id,
        nome: raiz.nome,
        plataforma: raiz.plataforma,
        topo: naSecao('topo'),
        projetos: pasta || organizacoes.length > 0 ? { pasta, organizacoes } : null,
        pastasDaPlataforma: raiz.plataforma ? daPlataforma.filter((p) => p !== pastaProjetos) : [],
      }
    })
    .filter((e) => mostrarVazias || e.topo.length > 0 || e.projetos !== null || e.pastasDaPlataforma.length > 0)
}

/** Documentos agrupados pela pasta onde estão (a listagem já aplica a regra de acesso). */
export function documentosPorPasta(docs: KbDocument[]): Map<number, KbDocument[]> {
  const mapa = new Map<number, KbDocument[]>()
  for (const d of docs) {
    if (d.folder_id == null) continue
    const lista = mapa.get(d.folder_id) ?? []
    lista.push(d)
    mapa.set(d.folder_id, lista)
  }
  return mapa
}

/** "A › B › C" a partir da lista plana — rótulo do destino ao mover documento. */
export function caminhoDe(nos: KbFolderNode[], id: number): string {
  const porId = new Map(nos.map((n) => [n.id, n]))
  const nomes: string[] = []
  const vistos = new Set<number>()
  let atual = porId.get(id)
  while (atual && !vistos.has(atual.id)) {
    vistos.add(atual.id)
    nomes.unshift(atual.nome)
    atual = atual.parent_id === null ? undefined : porId.get(atual.parent_id)
  }
  return nomes.join(' › ')
}

/**
 * Para onde a pasta `id` pode ir: as pastas da mesma organização, menos ela e as
 * descendentes — o backend recusa as duas (`folder_cycle`), e oferecer o que vai
 * ser recusado é erro de tela. O topo da organização fica de fora: quem chama o
 * oferece como opção própria.
 */
export function destinosParaMover(nos: KbFolderNode[], id: number): { id: number; caminho: string }[] {
  const alvo = nos.find((n) => n.id === id)
  if (!alvo) return []
  const mesmaOrg = nos.filter((n) => n.organization_id === alvo.organization_id)
  const fora = new Set<number>([id])
  let cresceu = true
  while (cresceu) {
    cresceu = false
    for (const n of mesmaOrg) {
      if (n.parent_id !== null && fora.has(n.parent_id) && !fora.has(n.id)) {
        fora.add(n.id)
        cresceu = true
      }
    }
  }
  return mesmaOrg
    .filter((n) => !fora.has(n.id))
    .map((n) => ({ id: n.id, caminho: caminhoDe(nos, n.id) }))
    .sort((a, b) => a.caminho.localeCompare(b.caminho, 'pt-BR'))
}
